import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: "", gstin: "", address: "", phone: "",
    contact_email: "", owner_name: "", password: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_email || !form.owner_name || !form.password) {
      toast.error("Business name, contact email, owner name and password are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/register-tenant`, form);
      setSubmitted(true);
      toast.success("Registration submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md" data-testid="register-success-card">
          <CardHeader>
            <CardTitle>Awaiting approval</CardTitle>
            <CardDescription>Your organisation registration has been submitted.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Once the platform admin approves your organisation you will be able to sign in with the email and password you just registered.
            </p>
            <Button data-testid="register-back-to-login" className="w-full" onClick={() => nav("/login")}>Back to Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-xl" data-testid="register-card">
        <CardHeader>
          <CardTitle className="text-2xl">Register Your Workshop</CardTitle>
          <CardDescription>Create a SynqMoto ERP tenant. A platform admin will approve your account before first login.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Business Name *</Label>
                <Input data-testid="reg-business-name" value={form.business_name} onChange={set("business_name")} required />
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input data-testid="reg-gstin" value={form.gstin} onChange={set("gstin")} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Textarea data-testid="reg-address" value={form.address} onChange={set("address")} rows={2} />
              </div>
              <div>
                <Label>Owner Name *</Label>
                <Input data-testid="reg-owner-name" value={form.owner_name} onChange={set("owner_name")} required />
              </div>
              <div>
                <Label>Contact Email *</Label>
                <Input data-testid="reg-contact-email" type="email" value={form.contact_email} onChange={set("contact_email")} required />
              </div>
              <div className="col-span-2">
                <Label>Password *</Label>
                <Input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required minLength={6} />
              </div>
            </div>
            <Button data-testid="register-submit-btn" type="submit" disabled={busy} className="w-full">
              {busy ? "Submitting..." : "Submit Registration"}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Already have an account? <Link to="/login" className="underline" data-testid="register-signin-link">Sign in</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
